import React, { useState } from 'react';
import { Filter, ExternalLink } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { projects } from '../data/mock';

const Projects = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  
  const categories = ['All', ...new Set(projects.map(p => p.category))];
  
  const filteredProjects = selectedCategory === 'All'
    ? projects
    : projects.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center bg-gradient-to-r from-slate-900 to-slate-700">
        <div className="container mx-auto px-4 z-10">
          <Badge className="mb-4 bg-blue-600 text-white border-0">Our Projects</Badge>
          <h1 className="text-5xl font-bold text-white mb-6">
            Portfolio of Excellence
          </h1>
          <p className="text-xl text-slate-200 max-w-3xl">
            Showcasing our expertise across premier medical institutions and healthcare facilities nationwide.
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white border-b border-slate-200 sticky top-20 z-40">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Filter size={20} className="text-slate-600" />
              <span className="text-slate-700 font-medium">Filter by:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <Button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  className={selectedCategory === category 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'border-slate-300 text-slate-700 hover:border-blue-600 hover:text-blue-600'
                  }
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <Card key={project.id} className="group overflow-hidden hover:shadow-2xl transition-all duration-300 border-slate-200">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <Badge className="absolute top-4 left-4 bg-blue-600 text-white border-0">
                    {project.category}
                  </Badge>
                  <Badge className="absolute top-4 right-4 bg-white text-slate-900 border-0">
                    {project.year}
                  </Badge>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-sm text-blue-600 font-medium mb-3">{project.client}</p>
                  <p className="text-slate-600 mb-4 text-sm leading-relaxed">{project.description}</p>
                  
                  <div className="space-y-2 mb-4">
                    <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Project Scope:</p>
                    <div className="flex flex-wrap gap-2">
                      {project.scope.slice(0, 3).map((item, idx) => (
                        <span key={idx} className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
                          {item}
                        </span>
                      ))}
                      {project.scope.length > 3 && (
                        <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
                          +{project.scope.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <span className="text-sm text-slate-500">Completed: {project.year}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    >
                      View Details <ExternalLink className="ml-1" size={14} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {filteredProjects.length === 0 && (
            <div className="text-center py-20">
              <p className="text-slate-500 text-lg">No projects found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">Project Highlights</h2>
              <p className="text-slate-600">
                Our track record speaks for our commitment to excellence and quality delivery.
              </p>
            </div>
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">150+</div>
                <div className="text-slate-600">Total Projects</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">10+</div>
                <div className="text-slate-600">AIIMS Projects</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">100%</div>
                <div className="text-slate-600">On-Time Delivery</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">24/7</div>
                <div className="text-slate-600">Support Available</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Notable Clients Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-50 text-blue-600 border-blue-200">Our Clients</Badge>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Trusted by Leading Institutions</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We're proud to have served premier medical institutions across India with our specialized services.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              'AIIMS Delhi',
              'AIIMS Bikaner',
              'AIIMS Bhubaneshwar',
              'Numed Hospital',
              'Neo Hospital Noida',
              'Lady Hardinge Medical College',
              'UCDC Guwahati',
              'Krsna Diagnostic'
            ].map((client, idx) => (
              <Card key={idx} className="border-slate-200 hover:shadow-lg transition-all hover:border-blue-300">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3">
                    <span className="text-blue-600 font-bold text-lg">{client.charAt(0)}</span>
                  </div>
                  <p className="text-slate-700 font-medium text-sm">{client}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-slate-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Have a Project in Mind?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Let's bring your vision to life with our expertise and commitment to excellence.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/contact">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-slate-100 h-12 px-8">
                Start Your Project
              </Button>
            </a>
            <a href="tel:9560043484">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600 h-12 px-8">
                Call Us Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects;
